import { useMemo, useState } from "react";
import { Calendar } from "@phosphor-icons/react";
import CalendarPicker from "./CalendarPicker";

/**
 * Reusable date-range chip filter used across Reports / Transactions / Forecast.
 *
 * Presets: all · last12 · 2024 · 2025 · 2026 · custom
 * `value` is `{ preset, from?, to? }`. `onChange({preset, from, to})` fires with ISO YYYY-MM-DD.
 *
 * Clicking "12 meses" or the calendar icon (or Custom) opens a rich CalendarPicker
 * with Day / Week / Month / Year granularity.
 */
export const CURRENT_YEAR = new Date().getFullYear();
export const YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

export function rangeForPreset(preset) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const iso = (d) => d.toISOString().slice(0, 10);

  if (preset === "all") return { from: null, to: null };
  if (preset === "last12") {
    const start = new Date(today);
    start.setMonth(start.getMonth() - 12);
    return { from: iso(start), to: `${yyyy}-${mm}-${dd}` };
  }
  const m = /^y(\d{4})$/.exec(preset);
  if (m) {
    const y = parseInt(m[1], 10);
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return { from: null, to: null };
}

export default function DateRangeChips({ value, onChange, testId = "date-range" }) {
  const { preset = "all", from = "", to = "" } = value || {};
  const [calendarOpen, setCalendarOpen] = useState(false);

  const options = useMemo(() => {
    const arr = [
      { key: "all", label: "Todo" },
      { key: "last12", label: "12 meses", opensCalendar: true },
      ...YEARS.map((y) => ({ key: `y${y}`, label: String(y) })),
      { key: "custom", label: "Custom", opensCalendar: true },
    ];
    return arr;
  }, []);

  const pickPreset = (opt) => {
    // Chips that open the calendar picker
    if (opt.opensCalendar) {
      setCalendarOpen(true);
      // For "last12" pre-apply the range so charts refresh even if user closes without picking
      if (opt.key === "last12") {
        const r = rangeForPreset("last12");
        onChange?.({ preset: "last12", from: r.from, to: r.to });
      }
      return;
    }
    const r = rangeForPreset(opt.key);
    onChange?.({ preset: opt.key, from: r.from, to: r.to });
  };

  return (
    <div data-testid={testId} className="inline-flex items-center gap-1.5 flex-wrap">
      <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
        {options.map((o) => {
          const active = preset === o.key;
          return (
            <button
              key={o.key}
              data-testid={`${testId}-${o.key}`}
              onClick={() => pickPreset(o)}
              className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
                active ? "on-solid bg-[#2563EB] text-white" : "text-white/45 hover:text-white/80"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Dedicated calendar icon — always available */}
      <button
        data-testid={`${testId}-open-calendar`}
        onClick={() => setCalendarOpen(true)}
        title="Elegir rango con calendario"
        className="w-8 h-8 rounded-lg bg-black/40 border border-white/8 hover:bg-white/10 hover:border-[#FACC15]/40 text-white/60 hover:text-[#FACC15] flex items-center justify-center transition"
      >
        <Calendar size={13} weight="bold" />
      </button>

      {/* Custom range chip: show the active from-to */}
      {preset === "custom" && (from || to) && (
        <span
          data-testid={`${testId}-active-custom`}
          className="text-[10px] font-mono px-2 py-1 rounded bg-white/6 border border-white/10 text-white/70"
        >
          {from || "…"} → {to || "…"}
        </span>
      )}

      <CalendarPicker
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onApply={(r) => onChange?.(r)}
        initialRange={{ from, to }}
      />
    </div>
  );
}
